// ============================================
// LightingComputePass — Base class for lighting compute passes
// ============================================
//
// Provides a consistent lifecycle pattern for compute passes in the
// lighting pipeline (VFX injection, tile culling, etc.).
//
// Usage:
// ```ts
// class MyComputePass extends LightingComputePass {
//   constructor(ctx: IRenderContext) {
//     super(ctx, 'MyPass');
//   }
//
//   protected onInitialize(): void { ... }
//   protected onDispose(): void { ... }
//
//   execute(resources): void { ... }
// }
// ```

import type { IRenderContext } from '../core/IRenderContext';
import type { ComputePipelineHandle, ShaderHandle, BufferHandle } from '../types';
import { NULL_HANDLE } from '../types';

// ============================================================================
// LightingComputePass
// ============================================================================

/**
 * Base class for compute passes in the lighting pipeline.
 * 
 * Provides lifecycle management (initialize/dispose) and common utilities
 * for compute shader-based operations like VFX light injection and tile culling.
 */
export abstract class LightingComputePass {
  protected ctx: IRenderContext;
  readonly name: string;

  /** Whether this pass is enabled. */
  enabled = true;

  private _initialized = false;

  constructor(ctx: IRenderContext, name: string) {
    this.ctx = ctx;
    this.name = name;
  }

  /**
   * Initialize pass resources. Called once before first execution.
   */
  initialize(): void {
    if (this._initialized) return;
    this.onInitialize();
    this._initialized = true;
  }

  /**
   * Cleanup pass resources.
   */
  dispose(): void {
    if (!this._initialized) return;
    this.onDispose();
    this._initialized = false;
  }

  /** Whether this pass is ready to execute. */
  get isReady(): boolean {
    return this._initialized && this.enabled;
  }

  /** Whether compute shaders are supported. */
  get supportsCompute(): boolean {
    return this.ctx.supportsCompute;
  }

  // ============================================
  // Override in subclasses
  // ============================================

  /**
   * Initialize GPU resources (shaders, pipelines, buffers).
   * Called once before first execution.
   */
  protected abstract onInitialize(): void;

  /**
   * Release GPU resources.
   */
  protected abstract onDispose(): void;

  // ============================================
  // Utilities
  // ============================================

  /**
   * Create a compute shader from WGSL source (already-compiled Slang→WGSL from the cache).
   */
  protected createShader(source: string, _label?: string): ShaderHandle {
    return this.ctx.createComputeShader!(source);
  }

  /**
   * Create a compute pipeline.
   */
  protected createPipeline(options: {
    shader: ShaderHandle;
    entryPoint?: string;
    workgroupSize?: [number, number, number];
    storageBufferCount?: number;
    label?: string;
  }): ComputePipelineHandle {
    return this.ctx.createComputePipeline!({
      shader: options.shader,
      entryPoint: options.entryPoint ?? 'main',
      workgroupSize: options.workgroupSize ?? [64, 1, 1],
      storageBufferCount: options.storageBufferCount ?? 1,
      label: options.label,
    });
  }

  /**
   * Create a uniform buffer.
   */
  protected createUniformBuffer(size: number, label?: string): BufferHandle {
    return this.ctx.createBuffer({
      size,
      usage: ['uniform', 'copy-dst'],
      label: label ?? "unlabeled-uniform-buffer",
    });
  }

  /**
   * Destroy a shader handle.
   */
  protected destroyShader(handle: ShaderHandle): void {
    if (handle !== NULL_HANDLE) {
      this.ctx.destroyShader(handle);
    }
  }

  /**
   * Destroy a compute pipeline handle.
   */
  protected destroyPipeline(handle: ComputePipelineHandle): void {
    if (handle !== NULL_HANDLE && this.ctx.destroyComputePipeline) {
      this.ctx.destroyComputePipeline(handle);
    }
  }

  /**
   * Destroy a buffer handle.
   */
  protected destroyBuffer(handle: BufferHandle): void {
    if (handle !== NULL_HANDLE) {
      this.ctx.destroyBuffer(handle);
    }
  }

  /**
   * Calculate workgroup count for a given element count.
   */
  protected workgroupCount(count: number, workgroupSize = 64): number {
    return Math.ceil(count / workgroupSize);
  }
}
